import React, { useMemo, useState, memo, useEffect, useRef } from "react";
import styled from "styled-components/macro";
import {
  Typography,
  Divider,
  Box,
  IconButton,
  Avatar,
  useTheme,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { titleize } from "inflected";
import { format as formatDate, isValid as isValidDate } from "date-fns";
import { isNullOrUndef } from "chart.js/helpers";
import { useBreakpoints } from "../../hooks/useBreakpoints";
import { formatBooleanTrueFalse } from "../../../../utils";

// Styled components
const PopupWrap = styled.div`
  height: ${({ height }) => height};
  overflow-y: scroll;
  max-width: ${({ width }) => width};
`;

const PopupTable = styled.table`
  border-radius: 5px;
  border-collapse: collapse;
  border: 1px solid #ccc;
  width: 100%;
`;

const PopupRow = styled.tr`
  border-radius: 5px;
  &:nth-of-type(even) {
    background-color: #eee;
  }
`;

const PopupCell = styled.td`
  padding: 3px 6px;
  margin: 0;
`;

const HeaderRow = styled.tr`
  background-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.contrastText};
  font-weight: bold;
  text-align: center;

  & td {
    padding: 5px;
  }
`;

const ToggleButtonsContainer = styled(Box)`
  position: absolute;
  top: 0;
  right: 20px;
  display: flex;
`;

const SizeToggleButton = ({ size, currentSize, onClick, label }) => {
  const theme = useTheme();
  const isActive = size === currentSize;

  return (
    <IconButton
      size="small"
      color={isActive ? "primary" : "default"}
      onClick={() => onClick(size)}
      aria-label={`${label} size`}
    >
      <Avatar
        style={{
          fontSize: "14px",
          width: 20,
          height: 20,
          backgroundColor: isActive
            ? theme.palette.secondary.main
            : theme.palette.grey[600],
        }}
      >
        {label}
      </Avatar>
    </IconButton>
  );
};

const getUniqueFeatures = (array, comparatorProperty1, comparatorProperty2) => {
  const existingFeatureKeys = new Set();
  return array.filter((el) => {
    const key = `${el[comparatorProperty1]}-${el.layer[comparatorProperty2]}`;
    if (existingFeatureKeys.has(key)) return false;
    existingFeatureKeys.add(key);
    return true;
  });
};

const applyFormatting = (value, formatOptions) => {
  if (!formatOptions) return value;

  let formattedValue = value;

  // Decimal precision
  if (formatOptions.decimalPrecision !== undefined && !isNaN(value)) {
    const precision = parseInt(formatOptions.decimalPrecision, 10);
    if (!isNaN(precision)) {
      formattedValue = parseFloat(formattedValue).toFixed(precision);
    }
  }

  // Convert to string for further manipulation
  formattedValue = formattedValue.toString();

  // Uppercase / Lowercase
  if (formatOptions.uppercase) formattedValue = formattedValue.toUpperCase();
  if (formatOptions.lowercase) formattedValue = formattedValue.toLowerCase();

  // Prefix / Suffix
  const parts = [];
  if (formatOptions.prefix) parts.push(formatOptions.prefix);
  parts.push(formattedValue);
  if (formatOptions.suffix) parts.push(formatOptions.suffix);
  formattedValue = parts.join("");

  // Date formatting
  if (formatOptions.dateFormat && isValidDate(new Date(value))) {
    formattedValue = formatDate(new Date(value), formatOptions.dateFormat);
  }

  return formattedValue;
};

const processTemplateString = (template, properties) => {
  // Split the template into static text and placeholders
  const parts = template.split(/({.*?})/g);

  let result = parts.map((part) => {
    // If it's a placeholder (e.g., {key}), process it
    if (part.startsWith("{") && part.endsWith("}")) {
      const key = part.slice(1, -1);
      const value = properties[key];

      if (value === undefined || value === null) return ""; // Return empty for undefined or null
      if (typeof value === "number" || typeof value === "boolean") return value; // Preserve original type
      return value.toString(); // Convert others to string
    }

    // Return static text as-is
    return part;
  });

  // If the template contains only a single placeholder, return its processed value directly
  if (parts.length === 3 && parts[0] === "" && parts[2] === "") {
    return result[1];
  }

  // Join parts back together for templates with mixed text and placeholders
  return result.join("");
};

const BUTTON_SIZE_CONFIG = {
  small: { height: "200px", width: "250px" },
  medium: { height: "300px", width: "500px" },
  large: { height: "500px", width: "800px" },
};

// Main Popup component
const Popup = ({ features, layers, currentUser, sizeRef }) => {
  const { isDownMd, isUpXs } = useBreakpoints();
  const [size, setSize] = useState(sizeRef?.current ?? null);

  const [page, setPage] = useState(1);
  const prevBreakpoint = useRef(null);

  // Determine initial size after hydration
  useEffect(() => {
    if (isUpXs && size === null) {
      prevBreakpoint.current = isDownMd;
      setSize(isDownMd ? "small" : "medium");
    }
  }, [isUpXs, isDownMd, size]);

  // Sync sizeRef with size
  useEffect(() => {
    if (size) sizeRef.current = size;
  }, [size, sizeRef]);

  useEffect(() => {
    if (!isUpXs || size === null) return;

    if (prevBreakpoint.current === null) {
      prevBreakpoint.current = isDownMd;
    } else {
      prevBreakpoint.current = !isDownMd;
    }
    if (isDownMd && isDownMd !== prevBreakpoint.current) {
      setSize((prevSize) => (prevSize === "medium" ? "small" : prevSize));
    } else if (isDownMd === false && isDownMd !== prevBreakpoint.current) {
      setSize((prevSize) => (prevSize === "small" ? "medium" : prevSize));
    }
  }, [isDownMd, isUpXs]); //eslint-disable-line react-hooks/exhaustive-deps

  // Handle button click to update size
  const handleSizeChange = (newSize) => {
    setSize(newSize); // Update state
    sizeRef.current = newSize; // Sync with ref
  };

  const uniqueFeatures = useMemo(
    () => getUniqueFeatures(features, "id", "id"),
    [features]
  );

  const feature = uniqueFeatures[page - 1] || null;

  const popupConfig = useMemo(() => {
    if (!feature) return {};
    const layer = layers.find((l) => l?.id === feature?.layer?.id);
    return layer?.lreProperties?.popup || {};
  }, [feature, layers]);

  const {
    excludeFields = [],
    excludePatterns = [],
    customLabels = {},
    sortOrder = [],
    reverseSortOrder = [],
    excludeAllExceptCustom = false,
    customFields = [],
    headers = [],
  } = popupConfig;

  // Only display admin fields if the user is admin or dev
  const adminOnlyFields = useMemo(() => {
    return currentUser?.isDeveloper || currentUser?.isAdmin
      ? []
      : popupConfig.adminOnlyFields ?? [];
  }, [currentUser, popupConfig]);

  // Build the popup title
  const titleField = useMemo(() => {
    if (!feature) return null;
    const titleValue = feature?.properties?.[popupConfig.titleField] ?? null;

    return (
      <>
        <Typography variant="h4" style={{ fontWeight: 600 }}>
          {titleize(feature?.layer?.source)}
        </Typography>
        {titleValue && (
          <>
            <Divider style={{ margin: "10px 0" }} />
            <Typography variant="h6">{titleValue}</Typography>
          </>
        )}
      </>
    );
  }, [feature, popupConfig]);

  // Process & filter fields for popup
  const popupData = useMemo(() => {
    if (!feature) return [];

    const { properties } = feature;
    const fieldsToDisplay = [];

    // 1. Gather any non-custom fields (if excludeAllExceptCustom = false)
    Object.entries(properties).forEach(([key, value]) => {
      const matchesPattern = excludePatterns.some((pattern) =>
        key.includes(pattern)
      );
      const shouldExclude =
        excludeFields.includes(key) ||
        matchesPattern ||
        adminOnlyFields.includes(key) ||
        isNullOrUndef(value);

      const displayLabel = customLabels[key] || titleize(key);

      const fieldContent = { value, type: undefined };

      if (excludeAllExceptCustom) {
        if (
          customLabels[key] ||
          sortOrder.includes(key) ||
          reverseSortOrder.includes(key)
        ) {
          fieldsToDisplay.push([displayLabel, fieldContent]);
        }
      } else if (!shouldExclude) {
        fieldsToDisplay.push([displayLabel, fieldContent]);
      }
    });

    // 2. Process customFields
    customFields.forEach((field) => {
      const { label, value: template, displayText, type, format } = field;

      // Process the main value
      const processedValue = processTemplateString(template, properties);

      // Skip if processedValue is null/undefined/empty
      if (isNullOrUndef(processedValue) || processedValue === "") return;

      // Process displayText, if provided
      let processedDisplayText = displayText
        ? processTemplateString(displayText, properties)
        : null;

      // Skip if displayText is given but resolves to null/undefined/empty
      if (
        displayText &&
        (isNullOrUndef(processedDisplayText) || processedDisplayText === "")
      ) {
        return;
      }

      // Format them
      const formattedValue = applyFormatting(processedValue, format);
      processedDisplayText = processedDisplayText
        ? applyFormatting(processedDisplayText, format)
        : formattedValue;

      fieldsToDisplay.push([
        label,
        {
          value: formattedValue,
          displayText: processedDisplayText,
          type,
        },
      ]);
    });

    // 3. Sort fields based on sortOrder & reverseSortOrder
    const sortedFields = fieldsToDisplay.sort((a, b) => {
      const [labelA] = a;
      const [labelB] = b;

      const indexA = sortOrder.indexOf(labelA);
      const indexB = sortOrder.indexOf(labelB);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      const reverseIndexA = reverseSortOrder.indexOf(labelA);
      const reverseIndexB = reverseSortOrder.indexOf(labelB);

      if (reverseIndexA !== -1 && reverseIndexB !== -1)
        return reverseIndexB - reverseIndexA;
      if (reverseIndexA !== -1) return 1;
      if (reverseIndexB !== -1) return -1;

      return 0;
    });

    return sortedFields;
  }, [
    feature,
    excludeFields,
    excludePatterns,
    customLabels,
    sortOrder,
    reverseSortOrder,
    excludeAllExceptCustom,
    adminOnlyFields,
    customFields,
  ]);

  const handlePageChange = (e, p) => setPage(p);

  if (!feature || !isUpXs || !size) return null;

  return (
    <>
      <PopupWrap
        height={BUTTON_SIZE_CONFIG[size].height}
        width={BUTTON_SIZE_CONFIG[size].width}
      >
        <ToggleButtonsContainer>
          <SizeToggleButton
            size="small"
            currentSize={size}
            onClick={() => handleSizeChange("small")}
            label="S"
          />
          <SizeToggleButton
            size="medium"
            currentSize={size}
            onClick={() => handleSizeChange("medium")}
            label="M"
          />
          <SizeToggleButton
            size="large"
            currentSize={size}
            onClick={() => handleSizeChange("large")}
            label="L"
          />
        </ToggleButtonsContainer>
        {titleField}
        <PopupTable>
          <tbody>
            {popupData.map(([label, { type, value, displayText }]) => {
              const headerConfig = headers.find((h) => h.propertyKey === label);
              return (
                <React.Fragment key={label}>
                  {headerConfig && (
                    <HeaderRow>
                      <td colSpan={2}>{headerConfig.headerTitle}</td>
                    </HeaderRow>
                  )}
                  <PopupRow>
                    <PopupCell>
                      <strong>{label}</strong>
                    </PopupCell>
                    <PopupCell>
                      {type === "url" ? (
                        <a
                          color="primary"
                          href={value}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {displayText}
                        </a>
                      ) : ["Attached Documents", "Attached Photos"].includes(
                          label
                        ) ? (
                        value.split(",").map((item) => (
                          <div key={item}>
                            <a
                              target="_blank"
                              href={item.split(";")[1]}
                              rel="noreferrer noopener"
                            >
                              {item.split(";")[0]}
                            </a>
                          </div>
                        ))
                      ) : (
                        // e.g. if it's text or something else
                        formatBooleanTrueFalse(value)
                      )}
                    </PopupCell>
                  </PopupRow>
                </React.Fragment>
              );
            })}
          </tbody>
        </PopupTable>
      </PopupWrap>
      <Pagination
        style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}
        count={uniqueFeatures.length}
        size={size || "medium"}
        siblingCount={size === "small" ? 0 : 1}
        page={page}
        variant="outlined"
        shape="rounded"
        color="primary"
        onChange={handlePageChange}
      />
    </>
  );
};

export default memo(Popup);
